import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, Renderer2 } from '@angular/core';
import { Subscription } from 'rxjs';
import { Hotspot, HotspotButtonType, HotspotDetailPosition, HotspotInteraction } from '../model/hotspot-models';
import { HotspotRegistry } from '../service/hotspot-registry.service';
import { HotspotService } from '../service/hotspot.service';

/* eslint-disable @angular-eslint/component-selector */
@Component({
  selector: '[uiHotspot]',
  templateUrl: './hotspot.component.html',
  styleUrls: ['./hotspot.component.scss']
})
/* eslint-enable @angular-eslint/component-selector */
export class HotspotComponent implements OnInit, OnDestroy {
  @Input('uiHotspot') uiHotspot: string | Hotspot;

  @Output() onHotspotButtonClick: EventEmitter<{ label: string; link: string; type: HotspotButtonType }> = new EventEmitter<{
    label: string;
    link: string;
    type: HotspotButtonType;
  }>();

  // holds hotspot detail dont fetch from server if exists
  public hotspotDetail: Hotspot;
  // Boolean value which can open and close detail box
  public isHotspotDetailActive: boolean = false;
  public isHotspotActive: boolean = false;

  private subscriptions: Array<Subscription> = [];
  // Holds click handler
  private globalClickHandler;
  // Holds name of class clicking which global click event to close detail box doesnt trigger
  private hostspotControllerClass: string = 'hotspot-controller';
  // Holds the list of class which is added to hotspot wrapper
  public hotspotClasses: Array<string> = [];

  // Holds the default settings of hotspot
  private defaultSettings: Hotspot = {
    id: '',
    detailPosition: HotspotDetailPosition.LEFTBOTTOM,
    openImmediately: false,
    text: 'Detail not found!'
  };

  private isTogglingDetail: boolean = false;

  // Drop class mappings to easily manipulate class on native element
  private detailPositionClasses = {
    left: 'hotspot-drop-left',
    top: 'hotspot-drop-top',
    bottom: 'hotspot-drop-bottom',
    right: 'hotspot-drop-right',
    center: 'hotspot-drop-center'
  };

  private classMapping = {
    [HotspotDetailPosition.LEFTTOP]: [this.detailPositionClasses.left, this.detailPositionClasses.top],
    [HotspotDetailPosition.LEFTBOTTOM]: [this.detailPositionClasses.left, this.detailPositionClasses.bottom],
    [HotspotDetailPosition.RIGHTTOP]: [this.detailPositionClasses.right, this.detailPositionClasses.top],
    [HotspotDetailPosition.RIGHTBOTTOM]: [this.detailPositionClasses.right, this.detailPositionClasses.bottom],
    [HotspotDetailPosition.CENTERTOP]: [this.detailPositionClasses.center, this.detailPositionClasses.top],
    [HotspotDetailPosition.CENTERBOTTOM]: [this.detailPositionClasses.center, this.detailPositionClasses.bottom]
  };

  constructor(
    private readonly hotspotService: HotspotService,
    private readonly hotspotRegistry: HotspotRegistry,
    private readonly renderer: Renderer2
  ) {}

  ngOnInit() {
    this.initializeHotspot();
    this.setupSubscriptions();
  }

  // We need to register event which could close the detail box if clicked outside of this hotspot box
  private initiateClickEventHandler() {
    if (!this.globalClickHandler) {
      this.globalClickHandler = this.renderer.listen(document, 'click', (event) => {
        // Check if clicked inside this hotspot or inside any hotspot controller. In both case we dont want to close hotspot.
        if (
          event.target.closest(`.${this.hostspotControllerClass}`) ||
          event.target.closest(`.hotspot-message`) ||
          (event.target.closest('.hotspot-container') && this.isTogglingDetail)
        ) {
          this.isTogglingDetail = false;
          return;
        }
        this.closeHotspotDetail();
      });
    }
  }

  // Destroy the event to close detail box whenever we dont need it
  private destroyClickEvent() {
    if (this.globalClickHandler) {
      this.globalClickHandler();
      this.globalClickHandler = null;
    }
  }

  // Destroy all registered observables and events on component destroy
  ngOnDestroy() {
    this.destroyClickEvent();
    this.subscriptions.forEach((subs) => {
      subs.unsubscribe();
    });
  }

  // Setter for other hotspot detail
  private initializeHotspot() {
    const hotspot = this.getHotspotDetail();
    const detailPosition: HotspotDetailPosition = hotspot.detailPosition || this.defaultSettings.detailPosition;
    const customClass: string = hotspot.customClass;
    const openImmediately: boolean = hotspot.openImmediately || this.defaultSettings.openImmediately;
    this.hotspotClasses = [];
    if (detailPosition) {
      this.hotspotClasses = this.classMapping[detailPosition];
    }
    if (customClass) {
      this.hotspotClasses = this.hotspotClasses.concat(customClass.split(','));
    }
    if (openImmediately && this.isHotspotActive) {
      this.openHotspotDetail();
    }
  }

  // Public event to open hotspot detail
  public openHotspotDetail() {
    if (!this.hotspotDetail) {
      this.initializeHotspot();
    }
    this.isHotspotActive = true;
    this.isHotspotDetailActive = true;
    this.initiateClickEventHandler();
  }

  // public event to close hotspot detail
  public closeHotspotDetail(): void {
    this.isHotspotDetailActive = false;
    this.destroyClickEvent();
  }

  // public event to toggle hotspot detail
  public toggleHotspotDetail(): void {
    this.isTogglingDetail = true;
    if (!this.isHotspotDetailActive) {
      this.openHotspotDetail();
    } else {
      this.closeHotspotDetail();
    }
  }

  public activateHotspot() {
    this.isHotspotActive = true;
  }

  public deactivateHotspot() {
    this.isHotspotActive = false;
  }

  // Event to handle any button clicks
  public btnClicked(btn: { label: string; link: string; type: HotspotButtonType }) {
    // Emit the event if button type is to emit
    if (btn.type && btn.type === HotspotButtonType.EMIT) {
      if (this.onHotspotButtonClick) {
        this.onHotspotButtonClick.emit(btn);
      }
      this.hotspotService.hotspotButtonClicked(this.hotspotDetail, btn);
    } else {
      // Redirect to new page with link
      window.open(btn.link, '_blank');
    }
  }

  // Invoke hotspot service method to get hotspot detail
  private getHotspotDetail(): Hotspot {
    this.hotspotDetail = this.hotspotRegistry.getHotspotDetail(this.getHotspotID()) || (this.uiHotspot as Hotspot);
    return this.hotspotDetail;
  }

  private getHotspotID(): string {
    if (typeof this.uiHotspot === 'string') {
      return this.uiHotspot;
    } else {
      return this.uiHotspot.id;
    }
  }

  private setupSubscriptions() {
    /**
     * Take action according to parameters received
     * if got boolean parameter, initiate hotspot if true, destroy if false
     */
    this.subscriptions.push(
      this.hotspotService.onHotspotsEnabledChange((enabled: boolean) => {
        this.isHotspotActive = enabled;
        this.initializeHotspot();
      })
    );

    this.subscriptions.push(
      this.hotspotService.onHotspotEnabledChange((action: HotspotInteraction) => {
        if (action.hotspotID === this.getHotspotID()) {
          this.isHotspotActive = action.open;
          this.initializeHotspot();
        }
      })
    );

    this.subscriptions.push(
      this.hotspotService.onHotspotDetailEnabledChange((action: HotspotInteraction) => {
        if (action.hotspotID === this.getHotspotID()) {
          if (!action.open) {
            this.closeHotspotDetail();
          } else {
            this.openHotspotDetail();
          }
        }
      })
    );
  }
}
